import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

const ID_STORAGE_KEY = 'recently_viewed_product_ids';
const MAX_PRODUCTS = 15;
const GRAPHQL_ENDPOINT = '/api/2024-04/graphql.json';
const GRAPHQL_CHUNK_SIZE = 10;
const GRAPHQL_VARIANT_LIMIT = 20;
const RECENTLY_VIEWED_QUERY = `
  query RecentlyViewedProducts($ids: [ID!]!, $variantCount: Int!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        handle
        title
        description
        onlineStoreUrl
        featuredImage {
          url
          altText
          width
          height
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: $variantCount) {
          nodes {
            id
            title
            sku
            availableForSale
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            image {
              url
              altText
              width
              height
            }
          }
        }
        stockStatus: metafield(namespace: "custom", key: "stock_status") { value }
        backorder: metafield(namespace: "custom", key: "backorder") { value }
        preorder: metafield(namespace: "custom", key: "preorder") { value }
        backOrderMessage: metafield(namespace: "custom", key: "back_order_message") { value }
        excludeFromRetail: metafield(namespace: "custom", key: "exclude_from_retail_side") { value }
        hideFromDealers: metafield(namespace: "custom", key: "hide_from_dealers_search") { value }
        custitem9: metafield(namespace: "custom", key: "custitem9") { value }
        custitem11: metafield(namespace: "custom", key: "custitem11") { value }
        custitem23: metafield(namespace: "custom", key: "custitem23") { value }
      }
    }
  }
`;

/**
 * @typedef {{
 *   id?: string | number,
 *   title?: string,
 *   sku?: string,
 *   price?: number,
 *   compare_at_price?: number,
 *   available?: boolean,
 *   availableForSale?: boolean,
 *   image?: Record<string, any>
 * }} RecentlyViewedVariant
 */

/**
 * @typedef {{
 *   id: string | number,
 *   title: string,
 *   url: string,
 *   handle?: string,
 *   price?: number,
 *   compare_at_price?: number,
 *   description?: string,
 *   variants?: Array<RecentlyViewedVariant>,
 *   selected_or_first_available_variant?: RecentlyViewedVariant | null,
 *   has_only_default_variant?: boolean,
 *   metafields?: Record<string, any>,
 *   metafield_values?: Record<string, any>,
 *   featured_media?: Record<string, any> | null,
 *   tracked_at?: string
 * }} RecentlyViewedProduct
 */

/**
 * @returns {string | undefined}
 */
function getCurrentProductGid() {
  if (typeof window === 'undefined') return undefined;
  const globalWindow = /** @type {Record<string, any>} */ (window);
  return (
    globalWindow.currentProductGid || (globalWindow.currentProductTracking && globalWindow.currentProductTracking.gid)
  );
}

/**
 * @returns {string[]}
 */
function getStoredProductIds() {
  try {
    const raw = localStorage.getItem(ID_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((value) => typeof value === 'string' && value.startsWith('gid://'));
    }
  } catch (error) {}
  return [];
}

/**
 * @param {string[]} ids
 */
function setStoredProductIds(ids) {
  try {
    const unique = ids.filter((value, index) => ids.indexOf(value) === index);
    localStorage.setItem(ID_STORAGE_KEY, JSON.stringify(unique.slice(0, MAX_PRODUCTS)));
  } catch (error) {}
}

function trackCurrentProduct() {
  const currentGid = getCurrentProductGid();
  if (!currentGid) return;

  const ids = getStoredProductIds();
  const updated = [currentGid, ...ids.filter((id) => id !== currentGid)];
  setStoredProductIds(updated);
}

/**
 * @template T
 * @param {T[]} array
 * @param {number} size
 * @returns {T[][]}
 */
function chunkArray(array, size) {
  const chunks = [];
  for (let index = 0; index < array.length; index += size) {
    chunks.push(array.slice(index, index + size));
  }
  return chunks;
}

/**
 * @param {string | undefined} amount
 * @returns {number | undefined}
 */
function formatAmountToCents(amount) {
  if (!amount) return undefined;
  const parsed = parseFloat(amount);
  if (Number.isNaN(parsed)) return undefined;
  return Math.round(parsed * 100);
}

/**
 * @param {Record<string, any> | null | undefined} metafield
 * @returns {string}
 */
function normalizeMetafieldValue(metafield) {
  if (!metafield) return '';
  if (typeof metafield.value === 'string') return metafield.value;
  if (metafield.value == null) return '';
  return String(metafield.value);
}

/**
 * @param {Record<string, any>} variant
 * @returns {RecentlyViewedVariant | null}
 */
function mapVariantNode(variant) {
  if (!variant) return null;

  return {
    id: variant.id,
    title: variant.title,
    sku: variant.sku,
    available: Boolean(variant.availableForSale),
    availableForSale: Boolean(variant.availableForSale),
    price: formatAmountToCents(variant.price?.amount),
    compare_at_price: formatAmountToCents(variant.compareAtPrice?.amount),
    image: variant.image
      ? {
          preview_image: {
            src: variant.image.url,
            alt: variant.image.altText || '',
            width: variant.image.width,
            height: variant.image.height,
          },
        }
      : undefined,
  };
}

/**
 * @param {Record<string, any>} node
 * @returns {RecentlyViewedProduct | null}
 */
function mapProductNode(node) {
  if (!node || node.id == null) return null;

  const variants = Array.isArray(node.variants?.nodes)
    ? /** @type {RecentlyViewedVariant[]} */ (node.variants.nodes.map(mapVariantNode).filter(Boolean))
    : [];

  const firstAvailable = variants.find((variant) => variant?.availableForSale) || variants[0] || null;
  const priceCents = formatAmountToCents(node.priceRange?.minVariantPrice?.amount) || firstAvailable?.price;
  const compareAtCents =
    formatAmountToCents(node.compareAtPriceRange?.minVariantPrice?.amount) || firstAvailable?.compare_at_price;

  const stockStatusValue = normalizeMetafieldValue(node.stockStatus);
  const backorderValue = normalizeMetafieldValue(node.backorder);
  const preorderValue = normalizeMetafieldValue(node.preorder);
  const backOrderMessageValue = normalizeMetafieldValue(node.backOrderMessage);
  const excludeFromRetailValue = normalizeMetafieldValue(node.excludeFromRetail);
  const hideFromDealersValue = normalizeMetafieldValue(node.hideFromDealers);
  const custitem9Value = normalizeMetafieldValue(node.custitem9);
  const custitem11Value = normalizeMetafieldValue(node.custitem11);
  const custitem23Value = normalizeMetafieldValue(node.custitem23);

  const metafieldsCustom = {
    stock_status: { value: stockStatusValue },
    backorder: { value: backorderValue },
    preorder: { value: preorderValue },
    back_order_message: { value: backOrderMessageValue },
    exclude_from_retail_side: { value: excludeFromRetailValue },
    hide_from_dealers_search: { value: hideFromDealersValue },
    custitem9: { value: custitem9Value },
    custitem11: { value: custitem11Value },
    custitem23: { value: custitem23Value },
  };

  const onlineUrl = node.onlineStoreUrl || `/products/${node.handle}`;
  const hasOnlyDefaultVariant = variants.length === 1;

  return {
    id: node.id,
    title: node.title,
    url: onlineUrl,
    handle: node.handle,
    description: node.description || '',
    price: priceCents,
    compare_at_price: compareAtCents,
    variants,
    selected_or_first_available_variant: firstAvailable,
    has_only_default_variant: hasOnlyDefaultVariant,
    featured_media: node.featuredImage
      ? {
          preview_image: {
            src: node.featuredImage.url,
            alt: node.featuredImage.altText || '',
            width: node.featuredImage.width,
            height: node.featuredImage.height,
          },
        }
      : null,
    metafield_values: {
      stock_status: stockStatusValue,
      backorder: backorderValue,
      preorder: preorderValue,
      back_order_message: backOrderMessageValue,
      excludeFromRetail: excludeFromRetailValue,
      hideFromDealers: hideFromDealersValue,
      custitem9: custitem9Value,
      custitem11: custitem11Value,
      custitem23: custitem23Value,
    },
    metafields: {
      custom: metafieldsCustom,
      'custom.stock_status': { value: stockStatusValue },
      'custom.backorder': { value: backorderValue },
      'custom.preorder': { value: preorderValue },
      'custom.back_order_message': { value: backOrderMessageValue },
      'custom.exclude_from_retail_side': { value: excludeFromRetailValue },
      'custom.hide_from_dealers_search': { value: hideFromDealersValue },
    },
    tracked_at: new Date().toISOString(),
  };
}

/**
 * @param {string[]} ids
 * @returns {Promise<RecentlyViewedProduct[]>}
 */
async function fetchProductsByIds(ids) {
  if (!ids.length) return [];

  const token = 'bb77c6812d79ea87e93ca8ac8b91a619';
  if (!token) {
    return [];
  }

  const chunks = chunkArray(ids, GRAPHQL_CHUNK_SIZE);
  /** @type {RecentlyViewedProduct[]} */
  const products = [];

  for (const chunk of chunks) {
    try {
      const response = await fetch(GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: RECENTLY_VIEWED_QUERY,
          variables: {
            ids: chunk,
            variantCount: GRAPHQL_VARIANT_LIMIT,
          },
        }),
      });

      if (!response.ok) {
        continue;
      }

      const json = await response.json();
      if (json.errors) {
      }

      if (json.data && Array.isArray(json.data.nodes)) {
        json.data.nodes.forEach((/** @type {any} */ node) => {
          const mapped = mapProductNode(node);
          if (mapped) {
            products.push(mapped);
          }
        });
      }
    } catch (error) {}
  }

  const unique = products.filter(
    (product, index, array) => array.findIndex((candidate) => candidate.id === product.id) === index,
  );

  const sorted = ids
    .map((id) => unique.find((product) => product.id === id))
    .filter((product) => product !== undefined);

  return /** @type {RecentlyViewedProduct[]} */ (sorted);
}

/**
 * @param {any} value
 * @returns {boolean}
 */
function isTruthyMetafield(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'yes' || normalized === 'true' || normalized === '1';
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return false;
}

/**
 * @param {string} prefix
 * @param {string} detail
 * @returns {string}
 */
function buildAvailabilityMessage(prefix, detail) {
  if (!prefix) return '';
  return detail ? `${prefix}, ${detail}` : prefix;
}

/**
 * @param {RecentlyViewedProduct | undefined} product
 * @returns {{ctaText: string, stockMessage: string, stockMessageType: string}}
 */
function getAvailabilityState(product) {
  const isSimpleProduct = product?.variants?.length === 1;
  const hasVariants = Array.isArray(product?.variants) && product.variants.length > 1;
  const defaultCta = isSimpleProduct ? 'Add to Cart' : hasVariants ? 'Select Options' : 'Shop Now';

  const rawStockStatus = getMetafieldValue(product, 'custom.stock_status');
  const stockStatus = typeof rawStockStatus === 'string' ? rawStockStatus.toLowerCase().replace(/\s+/g, '') : '';
  const preorderEnabled = isTruthyMetafield(getMetafieldValue(product, 'custom.preorder'));
  const backOrderMessageRaw = getMetafieldValue(product, 'custom.back_order_message');
  const backOrderMessage =
    typeof backOrderMessageRaw === 'string' ? backOrderMessageRaw : (backOrderMessageRaw?.toString?.() ?? '');

  let ctaText = defaultCta;
  let stockMessage = '';
  let stockMessageType = '';

  if (stockStatus === 'instock' || stockStatus === '') {
  } else if (stockStatus === 'outofstock') {
    if (preorderEnabled) {
      stockMessageType = 'preorder';
      stockMessage = buildAvailabilityMessage('Available on Preorder', backOrderMessage);
    } else {
      stockMessageType = 'backorder';
      stockMessage = buildAvailabilityMessage('Available on backorder', backOrderMessage);
    }
  }

  return {
    ctaText,
    stockMessage,
    stockMessageType,
  };
}

/**
 * @param {RecentlyViewedProduct | undefined} product
 * @param {string} key
 * @returns {any}
 */
function getMetafieldValue(product, key) {
  if (!product || !key) return '';
  const [namespace, name] = key.split('.');
  if (!namespace || !name) {
    return '';
  }
  const metafields = product.metafields || {};
  let field = undefined;

  const namespaceEntry = metafields[namespace];
  if (namespaceEntry && typeof namespaceEntry === 'object') {
    field = namespaceEntry[name];
  }

  if (field === undefined) {
    field = metafields[key];
  }

  if (field === undefined && product.metafield_values && typeof product.metafield_values === 'object') {
    field = product.metafield_values[name];
  }

  if (field && typeof field === 'object') {
    if ('value' in field && field.value !== undefined && field.value !== null) {
      return field.value;
    }
    if ('text' in field && field.text !== undefined && field.text !== null) {
      return field.text;
    }
  }

  return field ?? '';
}

function hideSection() {
  const section = /** @type {HTMLElement | null} */ (document.querySelector('recently-viewed-products'));
  if (section) {
    section.style.display = 'none';
    const parentSection = section.closest('.section');
    if (parentSection instanceof HTMLElement) {
      parentSection.style.setProperty('display', 'none');
    }
  }
}

/**
 * @param {RecentlyViewedProduct[]} products
 */
function renderProducts(products) {
  const recentlyViewedSection = /** @type {HTMLElement | null} */ (document.querySelector('recently-viewed-products'));
  if (!recentlyViewedSection) {
    return;
  }

  const desktopGrid = recentlyViewedSection.querySelector('[data-testid="resource-list-grid"]');
  const mobileCarousel = recentlyViewedSection.querySelector('slideshow-slides');

  if (!desktopGrid || !mobileCarousel) {
    return;
  }

  desktopGrid.innerHTML = '';
  mobileCarousel.innerHTML = '';

  const validProducts = products.filter((product) => product?.id && product?.title && product?.url);

  validProducts.forEach((product, index) => {
    try {
      desktopGrid.appendChild(createProductCard(product, index));
      mobileCarousel.appendChild(createMobileSlide(product, index));
    } catch (error) {}
  });

  setTimeout(() => {
    const slideshowComponent = recentlyViewedSection.querySelector('slideshow-component');
    if (slideshowComponent) {
      const parent = slideshowComponent.parentNode;
      if (parent) {
        const nextSibling = slideshowComponent.nextSibling;
        parent.removeChild(slideshowComponent);
        parent.insertBefore(slideshowComponent, nextSibling);
      }
    }
  }, 100);

  setTimeout(() => {
    if (window.__b2bCartInitFunction) {
      window.__b2bCartInitFunction();
    }
    if (isB2BCustomer()) {
      initSparkPricing();
    }
  }, 200);
}

function extractNumericId(id) {
  if (!id) return id;
  const idString = String(id);
  if (idString.startsWith('gid://')) {
    const parts = idString.split('/');
    return parts[parts.length - 1] || idString;
  }
  return idString;
}

async function initSparkPricing() {
  if (!window.spark || !window.spark.getPricingForVariant) {
    setTimeout(() => {
      if (window.spark && window.spark.getPricingForVariant) {
        initSparkPricing();
      }
    }, 500);
    return;
  }

  const priceContainers = document.querySelectorAll('[data-spark-price]:not([data-pricing-initialized])');

  for (const priceContainer of priceContainers) {
    let productId = priceContainer.dataset.productId;
    const variantSku = priceContainer.dataset.variantSku;
    const hasVariants = priceContainer.dataset.hasVariants === 'true';

    if (!productId || !variantSku) continue;

    productId = extractNumericId(productId);

    priceContainer.dataset.pricingInitialized = 'true';

    try {
      const pricing = await window.spark.getPricingForVariant(productId, variantSku);

      if (pricing && pricing.price !== null && pricing.price !== undefined) {
        const priceValue = typeof pricing.price === 'number' ? pricing.price : pricing.price?.value || pricing.price;
        const currencyCode = pricing.currencyCode || 'USD';

        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode.toUpperCase(),
        }).format(priceValue);

        const priceElement = priceContainer.querySelector('.price');
        if (priceElement) {
          priceElement.textContent = formattedPrice;
        }

        if (hasVariants) {
          const priceLabel = priceContainer.querySelector('.starting-price-label');
          if (!priceLabel) {
            const label = document.createElement('span');
            label.className = 'starting-price-label';
            label.textContent = 'Starting at ';
            priceContainer.insertBefore(label, priceElement);
          } else {
            priceLabel.textContent = 'Starting at ';
          }
        }

        priceContainer.style.opacity = '1';
      } else {
        const priceElement = priceContainer.querySelector('.price');
        if (priceElement) {
          priceElement.textContent = '';
        }
        const priceLabel = priceContainer.querySelector('.starting-price-label');
        if (priceLabel) {
          priceLabel.textContent = '';
        }
        priceContainer.style.opacity = '0';
      }
    } catch (error) {}
  }
}

/**
 * @param {RecentlyViewedProduct} product
 * @param {number} index
 */
function createProductCard(product, index) {
  const item = document.createElement('div');
  item.className = 'resource-list__item';
  item.appendChild(createProductCardContent(product, index));
  return item;
}

/**
 * @param {RecentlyViewedProduct} product
 * @param {number} index
 */
function createMobileSlide(product, index) {
  const slide = document.createElement('slideshow-slide');
  slide.setAttribute('ref', 'slides[]');
  slide.setAttribute('data-slide-index', String(index));
  slide.setAttribute('data-slide-position', String(index));
  slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
  slide.style.setProperty('view-timeline-name', `--slide-${index}`);
  slide.style.setProperty('--product-media-fit', 'cover');
  slide.className = 'resource-list__slide';
  slide.appendChild(createProductCard(product, index));
  return slide;
}

/**
 * @param {RecentlyViewedProduct} product
 * @param {number} index
 */
function createProductCardContent(product, index) {
  const productCard = document.createElement('product-card');
  productCard.className = 'product-card';
  productCard.setAttribute('data-product-id', String(product.id));
  productCard.setAttribute('data-product-variants-size', String(product.variants?.length || 0));
  productCard.setAttribute('data-product-transition', 'false');

  const link = document.createElement('a');
  link.href = product.url;
  link.className = 'product-card__link';
  link.setAttribute('ref', 'productCardLink');

  const hiddenSpan = document.createElement('span');
  hiddenSpan.className = 'visually-hidden';
  hiddenSpan.textContent = product.title;
  link.appendChild(hiddenSpan);
  productCard.appendChild(link);

  const content = document.createElement('div');
  content.className =
    'product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style';
  content.style.cssText = `
    --border-width: 1px; 
    --border-style: none; 
    --border-color: rgb(var(--color-border-rgb) / 1.0); 
    --border-radius: 0px; 
    --flex-direction: column; 
    --flex-wrap: nowrap;
    --horizontal-alignment: ; 
    --vertical-alignment: ;
    --vertical-alignment-mobile: ;
    --padding-block-start: 0px;
    --padding-block-end: 8px;
    --padding-inline-start: 0px;
    --padding-inline-end: 0px;
    --product-card-gap: 8px;
    --quick-add-display: none;
    --quick-add-mobile-display: none;
    --zoom-out-padding-block-end: min(var(--padding-xs), 5.6px);
  `;

  if (product.featured_media) {
    content.appendChild(createProductImage(product));
  }

  const title = document.createElement('h3');
  title.className = 'product-title';
  title.textContent = product.title;
  content.appendChild(title);

  if (product.selected_or_first_available_variant?.sku) {
    const skuContainer = document.createElement('div');
    skuContainer.className = 'product-sku-container';
    const sku = document.createElement('span');
    sku.className = 'product-sku';
    sku.textContent = `SKU: ${product.selected_or_first_available_variant.sku}`;
    skuContainer.appendChild(sku);
    content.appendChild(skuContainer);
  }

  if (product.description) {
    const descriptionContainer = document.createElement('div');
    descriptionContainer.className = 'custom-product-short-description spacing-style';
    descriptionContainer.style.cssText =
      '--padding-block-start: 0px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px;';
    const truncatedDescription =
      product.description.length > 100 ? `${product.description.substring(0, 100)}...` : product.description;
    descriptionContainer.innerHTML = `<p>${truncatedDescription}</p>`;
    content.appendChild(descriptionContainer);
  }

  const priceContainer = document.createElement('div');
  priceContainer.className = 'custom-starting-price spacing-style';
  priceContainer.setAttribute('data-block-id', `recently-viewed-price-${index}`);
  priceContainer.setAttribute('data-product-id', String(product.id));
  priceContainer.style.cssText =
    '--padding-block-start: 0px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px; transition: opacity 0.3s ease;';

  const isSimpleProduct = product.variants?.length === 1;
  const isB2B = isB2BCustomer();

  if (isB2B) {
    const variant = product.selected_or_first_available_variant;
    if (variant?.sku) {
      priceContainer.setAttribute('data-variant-sku', variant.sku);
      priceContainer.setAttribute('data-spark-price', '');
      priceContainer.style.opacity = '0';
      if (!isSimpleProduct) {
        priceContainer.setAttribute('data-has-variants', 'true');
      }
    }
  }

  if (!isSimpleProduct) {
    const priceLabel = document.createElement('span');
    priceLabel.className = 'starting-price-label';
    priceLabel.textContent = 'Starting at ';
    priceContainer.appendChild(priceLabel);
  }

  const price = document.createElement('span');
  price.className = 'price';
  price.textContent = formatPrice(product.price);
  priceContainer.appendChild(price);
  content.appendChild(priceContainer);

  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'custom-select-options-button spacing-style';
  buttonContainer.style.cssText =
    '--padding-block-start: 0px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px;';

  const availability = getAvailabilityState(product);
  const variant = product.selected_or_first_available_variant;
  const isSimpleProductForButton = product.variants?.length === 1;

  if (isSimpleProductForButton && variant) {
    const canAddToCart = Boolean(variant.availableForSale);

    if (isB2B) {
      const variantSku = variant.sku || '';
      const productVariantMedia = variant.image?.preview_image?.src || product.featured_media?.preview_image?.src || '';

      const b2bWrapper = document.createElement('div');
      b2bWrapper.className = 'custom-select-options-button__b2b-wrapper';
      b2bWrapper.setAttribute('data-variant-sku', variantSku);
      b2bWrapper.setAttribute('data-quantity', '1');

      const liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'assertive');
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('data-b2b-live-region', '');
      b2bWrapper.appendChild(liveRegion);

      const addToCartComponent = document.createElement('add-to-cart-component');
      addToCartComponent.setAttribute('data-product-variant-media', productVariantMedia);
      addToCartComponent.setAttribute('data-add-to-cart-animation', 'true');

      const button = document.createElement('button');
      button.setAttribute('type', 'button');
      button.setAttribute('ref', 'addToCartButton');
      button.className = 'button button--select-options button-primary';
      button.setAttribute('data-b2b-add-to-cart', '');
      if (!canAddToCart) {
        button.setAttribute('disabled', 'disabled');
        button.classList.add('button--sold-out');
      }

      const addToCartText = document.createElement('span');
      addToCartText.className = 'add-to-cart-text';

      if (canAddToCart) {
        const svgWrapper = document.createElement('span');
        svgWrapper.className = 'svg-wrapper add-to-cart-icon';
        svgWrapper.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"></path><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width)" d="M14.608 12.563v5m2.5-2.5h-5"></path></svg>';
        addToCartText.appendChild(svgWrapper);
      }

      const textContent = document.createElement('span');
      textContent.className = 'add-to-cart-text__content';
      textContent.textContent = availability.ctaText || 'Add to Cart';
      addToCartText.appendChild(textContent);

      button.appendChild(addToCartText);

      const addedText = document.createElement('span');
      addedText.setAttribute('aria-hidden', 'true');
      addedText.className = 'add-to-cart-text--added';
      const addedSvgWrapper = document.createElement('span');
      addedSvgWrapper.className = 'svg-wrapper add-to-cart-icon--added';
      addedSvgWrapper.innerHTML =
        '<svg class="icon-checkmark" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 18C1.45 18 0.979167 17.8042 0.5875 17.4125C0.195833 17.0208 0 16.55 0 16V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H16C16.55 0 17.0208 0.195833 17.4125 0.5875C17.8042 0.979167 18 1.45 18 2V16C18 16.55 17.8042 17.0208 17.4125 17.4125C17.0208 17.8042 16.55 18 16 18H2ZM2 16H16V2H2V16Z" fill="#323232"></path></svg>';
      addedText.appendChild(addedSvgWrapper);
      const addedSpan = document.createElement('span');
      addedSpan.textContent = 'Added';
      addedText.appendChild(addedSpan);
      button.appendChild(addedText);

      addToCartComponent.appendChild(button);
      b2bWrapper.appendChild(addToCartComponent);

      const errorElement = document.createElement('span');
      errorElement.className = 'product-form-text__error hidden';
      errorElement.setAttribute('data-b2b-error', '');
      const errorSvgWrapper = document.createElement('span');
      errorSvgWrapper.className = 'svg-wrapper product-form-icon--error';
      errorSvgWrapper.innerHTML =
        '<svg class="icon icon-error" viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="var(--icon-stroke-width)"></circle><circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle><path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path><path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"></path></svg>';
      errorElement.appendChild(errorSvgWrapper);
      b2bWrapper.appendChild(errorElement);

      buttonContainer.appendChild(b2bWrapper);
    } else {
      const productFormId = `SelectOptions-ProductForm-recently-viewed-${index}`;

      let numericVariantId = String(variant.id);
      if (numericVariantId.startsWith('gid://')) {
        const parts = numericVariantId.split('/');
        numericVariantId = parts[parts.length - 1] || numericVariantId;
      }

      const productFormComponent = document.createElement('product-form-component');
      productFormComponent.setAttribute('data-section-id', 'recently-viewed');
      productFormComponent.setAttribute('data-product-id', String(product.id));
      productFormComponent.setAttribute('data-product-url', product.url);
      productFormComponent.setAttribute('data-has-only-default-variant', 'true');
      productFormComponent.setAttribute('data-variant-price', String(variant.price || product.price || 0));
      productFormComponent.setAttribute('data-variant-id', numericVariantId);
      productFormComponent.setAttribute('data-variant-sku', variant.sku || '');
      productFormComponent.setAttribute('data-quantity-default', '1');

      const liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'assertive');
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('ref', 'liveRegion');
      productFormComponent.appendChild(liveRegion);

      const form = document.createElement('form');
      form.setAttribute('action', `/cart/add`);
      form.setAttribute('method', 'post');
      form.setAttribute('enctype', 'multipart/form-data');
      form.setAttribute('id', productFormId);
      form.setAttribute('novalidate', 'novalidate');
      form.setAttribute('data-type', 'add-to-cart-form');

      const variantInput = document.createElement('input');
      variantInput.setAttribute('type', 'hidden');
      variantInput.setAttribute('name', 'id');
      variantInput.setAttribute('ref', 'variantId');
      variantInput.setAttribute('value', numericVariantId);
      form.appendChild(variantInput);

      const quantityInput = document.createElement('input');
      quantityInput.setAttribute('type', 'hidden');
      quantityInput.setAttribute('name', 'quantity');
      quantityInput.setAttribute('value', '1');
      form.appendChild(quantityInput);

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const submitButton = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
        const originalButtonText = submitButton?.textContent || 'Add to Cart';

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Added';
        }

        const formData = new FormData(form);
        let variantId = formData.get('id');
        const quantity = formData.get('quantity') || '1';

        if (!variantId) {
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalButtonText;
          }
          return;
        }

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        /** @type {string[]} */
        const sectionsToUpdate = [];
        cartItemsComponents.forEach((item) => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionsToUpdate.push(item.dataset.sectionId);
          }
        });

        const headerSection = document.querySelector('.shopify-section[class*="header"]');
        if (headerSection && headerSection.id) {
          const headerSectionId = headerSection.id.replace('shopify-section-', '');
          if (headerSectionId && !sectionsToUpdate.includes(headerSectionId)) {
            sectionsToUpdate.push(headerSectionId);
          }
        }

        const cartDrawerComponents = document.querySelectorAll('cart-drawer-component');
        cartDrawerComponents.forEach((item) => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            const sectionId = item.dataset.sectionId;
            if (!sectionsToUpdate.includes(sectionId)) {
              sectionsToUpdate.push(sectionId);
            }
          }
        });

        const cartFormData = new FormData(form);

        const sku = (productFormComponent.dataset.variantSku || '').trim();
        if (sku) {
          cartFormData.append('properties[SKU]', sku);
        }

        if (sectionsToUpdate.length > 0) {
          cartFormData.append('sections', sectionsToUpdate.join(','));
        }

        try {
          const cartAddUrl = (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) || '/cart/add.js';

          const fetchCfg = fetchConfig('javascript', { body: cartFormData });

          const response = await fetch(cartAddUrl, {
            ...fetchCfg,
            headers: {
              ...fetchCfg.headers,
              Accept: 'text/html',
            },
          });

          const result = await response.json();

          if (result.status || result.errors) {
            const errorMessage = result.message || result.errors || result.description || 'Error adding to cart';
            const errorElement = productFormComponent.querySelector('[ref="addToCartTextError"]');
            if (errorElement) {
              errorElement.classList.remove('hidden');
              const textNode = errorElement.childNodes[2];
              if (textNode) {
                textNode.textContent = errorMessage;
              } else {
                errorElement.appendChild(document.createTextNode(errorMessage));
              }
            }

            if (submitButton) {
              submitButton.disabled = false;
              submitButton.textContent = originalButtonText;
            }
            return;
          }

          const formId = form.getAttribute('id') || productFormId;
          const variantIdFromForm = cartFormData.get('id');

          const cartAddEvent = new CartAddEvent({}, formId, {
            source: 'product-form-component',
            itemCount: Number(cartFormData.get('quantity')) || 1,
            productId: String(product.id),
            variantId: String(variantIdFromForm),
            sections: result.sections || {},
          });

          document.dispatchEvent(cartAddEvent);

          const errorElement = productFormComponent.querySelector('[ref="addToCartTextError"]');
          if (errorElement) {
            errorElement.classList.add('hidden');
          }

          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalButtonText;
          }
        } catch (error) {
          const errorElement = productFormComponent.querySelector('[ref="addToCartTextError"]');
          if (errorElement) {
            errorElement.classList.remove('hidden');
            const textNode = errorElement.childNodes[2];
            if (textNode) {
              textNode.textContent = 'Error adding to cart. Please try again.';
            } else {
              errorElement.appendChild(document.createTextNode('Error adding to cart. Please try again.'));
            }
          }

          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalButtonText;
          }
        }
      });

      const addToCartButton = document.createElement('button');
      addToCartButton.setAttribute('type', 'submit');
      addToCartButton.className = 'button button--select-options button-primary';
      if (!canAddToCart) {
        addToCartButton.setAttribute('disabled', 'disabled');
        addToCartButton.classList.add('button--sold-out');
      }
      addToCartButton.textContent = availability.ctaText || 'Add to Cart';
      form.appendChild(addToCartButton);

      const errorText = document.createElement('span');
      errorText.className = 'product-form-text__error hidden';
      errorText.setAttribute('ref', 'addToCartTextError');
      errorText.innerHTML = '<span class="svg-wrapper product-form-icon--error"></span>';
      form.appendChild(errorText);

      productFormComponent.appendChild(form);
      buttonContainer.appendChild(productFormComponent);
    }
  } else {
    const addToCartLink = document.createElement('a');
    addToCartLink.href = product.url;
    addToCartLink.className = 'button button--select-options button-primary';
    addToCartLink.textContent = availability.ctaText || 'Shop Now';
    buttonContainer.appendChild(addToCartLink);
  }

  content.appendChild(buttonContainer);

  productCard.appendChild(content);
  return productCard;
}

/**
 * @param {RecentlyViewedProduct} product
 */
function createProductImage(product) {
  const imageContainer = document.createElement('div');
  imageContainer.className = 'custom-product-image spacing-style';
  imageContainer.style.cssText =
    '--padding-block-start: 0px; --padding-block-end: 0px; --padding-inline-start: 0px; --padding-inline-end: 0px;';

  const link = document.createElement('a');
  link.href = product.url;
  link.className = 'custom-product-image__link';

  const wrapper = document.createElement('div');
  wrapper.className = 'custom-product-image__wrapper';

  if (isProductOnSale(product)) {
    const saleLabel = document.createElement('div');
    saleLabel.className = 'product-card__sale-label';
    saleLabel.textContent = 'SALE';
    wrapper.appendChild(saleLabel);
  }

  const img = document.createElement('img');
  const preview = product.featured_media?.preview_image || {};
  img.src = preview.src || '';
  img.alt = preview.alt || product.title;
  img.width = 600;
  img.height = 600;
  img.loading = 'lazy';
  img.className = 'custom-product-image__img';
  img.sizes = '(min-width: 1080px) 300px, 250px';

  if (preview.src) {
    const widths = [200, 300, 400, 500, 600];
    const srcSetEntries = widths.map(
      (width) => `${preview.src}${preview.src.includes('?') ? '&' : '?'}width=${width} ${width}w`,
    );
    img.srcset = srcSetEntries.join(', ');
  }

  wrapper.appendChild(img);
  link.appendChild(wrapper);
  imageContainer.appendChild(link);
  return imageContainer;
}

/**
 * @param {RecentlyViewedProduct | undefined} product
 */
function isProductOnSale(product) {
  if (!product) return false;

  if (product.compare_at_price && product.price && product.compare_at_price > product.price) {
    return true;
  }

  if (product.variants && Array.isArray(product.variants)) {
    return product.variants.some(
      /** @param {RecentlyViewedVariant} variant */
      (variant) =>
        Boolean(variant?.compare_at_price) &&
        Boolean(variant?.price) &&
        Number(variant.compare_at_price) > Number(variant.price),
    );
  }

  return false;
}

/**
 * @param {number | undefined} price
 */
function formatPrice(price) {
  if (!price && price !== 0) return 'Price unavailable';
  return `$${(price / 100).toFixed(2)}`;
}

function isB2BCustomer() {
  const section = /** @type {HTMLElement | null} */ (document.querySelector('recently-viewed-products'));

  if (!section) {
    return false;
  }

  const customerId = section.dataset.customerId;
  const customerTags = section.dataset.customerTags;
  const sparklayerAuth = section.dataset.sparklayerAuth;

  if (!customerId) {
    return false;
  }

  if (sparklayerAuth === 'true') {
    return true;
  }

  if (customerTags) {
    const tags = /** @type {string[]} */ (
      customerTags
        .toLowerCase()
        .split(',')
        .map((/** @type {string} */ t) => t.trim())
    );
    const b2bTags = ['b2b', 'wholesale', 'master_dealer', 'distributor'];
    const hasB2BTag = tags.some((/** @type {string} */ tag) => b2bTags.includes(tag));
    return hasB2BTag;
  }

  return false;
}

/**
 * @param {RecentlyViewedProduct} product
 * @returns {boolean}
 */
function isCustitemRestricted(product) {
  const section = /** @type {HTMLElement | null} */ (document.querySelector('recently-viewed-products'));
  if (!section || !section.dataset.customerId) {
    return false;
  }

  const yesValues = ['y', 'yes', 'true'];
  const normalize = (/** @type {any} */ value) =>
    value === true ? 'true' : typeof value === 'string' ? value.trim().toLowerCase() : '';

  const tags = (section.dataset.customerTags || '')
    .toLowerCase()
    .split(',')
    .map((/** @type {string} */ t) => t.trim())
    .filter(Boolean);

  const isEcommerceDealer = tags.includes('dealer') || tags.includes('dealer_30');
  const isDistributor = tags.includes('distributor');
  const isB2B =
    isEcommerceDealer ||
    isDistributor ||
    section.dataset.sparklayerAuth === 'true' ||
    ['b2b', 'wholesale', 'master_dealer'].some((tag) => tags.includes(tag));

  const values = product.metafield_values || {};
  const custitem9 = normalize(values.custitem9);
  const custitem11 = normalize(values.custitem11);
  const custitem23 = normalize(values.custitem23);

  if (isEcommerceDealer && !yesValues.includes(custitem9)) {
    return true;
  }

  if (isDistributor && yesValues.includes(custitem11)) {
    return true;
  }

  if (isB2B && yesValues.includes(custitem23)) {
    return true;
  }

  return false;
}

/**
 * Filter products based on B2B restrictions
 * @param {RecentlyViewedProduct[]} products
 * @returns {RecentlyViewedProduct[]}
 */
function filterRestrictedProducts(products) {
  const isDealer = isB2BCustomer();

  return products.filter((product) => {
    if (product.handle === 'custom-color-option') {
      return false;
    }

    const excludeMetafield =
      product.metafield_values?.excludeFromRetail || product.metafields?.custom?.exclude_from_retail_side;

    if ((excludeMetafield === 'true' || excludeMetafield === true) && !isDealer) {
      return false;
    }

    const hideFromDealersMetafield = product.metafield_values?.hideFromDealers;

    if ((hideFromDealersMetafield === 'true' || hideFromDealersMetafield === true) && isDealer) {
      return false;
    }

    if (isCustitemRestricted(product)) {
      return false;
    }

    return true;
  });
}

async function loadRecentlyViewedProducts() {
  const ids = getStoredProductIds();
  const currentGid = getCurrentProductGid();
  const filteredIds = ids.filter((id) => id !== currentGid);

  const section = /** @type {HTMLElement | null} */ (document.querySelector('recently-viewed-products'));
  if (!section) return;

  const productsToShow = parseInt(section.dataset.productsToShow || '5', 10) || 5;

  const idsToFetch = filteredIds.slice(0, productsToShow * 2);

  if (!idsToFetch.length) {
    hideSection();
    return;
  }

  const products = await fetchProductsByIds(idsToFetch);

  const visibleProducts = filterRestrictedProducts(products);

  if (!visibleProducts.length) {
    hideSection();
    return;
  }

  renderProducts(visibleProducts.slice(0, productsToShow));
}

async function initRecentlyViewedProducts() {
  try {
    trackCurrentProduct();
    loadRecentlyViewedProductsWhenVisible();
  } catch (error) {
    hideSection();
  }
}

/**
 * Defers the recommendations fetch/render until the recently-viewed-products
 * section is near the viewport, to avoid rendering an offscreen product grid on page load.
 */
function loadRecentlyViewedProductsWhenVisible() {
  const section = /** @type {HTMLElement | null} */ (document.querySelector('recently-viewed-products'));
  const loader = window.GYDeferredLoader;

  if (!loader) {
    void loadRecentlyViewedProducts().catch(() => hideSection());
    return;
  }

  let hasLoaded = false;
  const loadOnce = () => {
    if (hasLoaded) return;
    hasLoaded = true;
    void loadRecentlyViewedProducts().catch(() => hideSection());
  };

  loader.onVisible(section, loadOnce, '400px');
  loader.onFirstInteraction(loadOnce, 6000);
  loader.runOnIdle(loadOnce, 7000);
}

export const RecentlyViewed = {
  getProducts: () => {
    try {
      return /** @type {string[]} */ (getStoredProductIds());
    } catch (error) {
      return [];
    }
  },
  addProduct: (/** @type {string} */ id) => {
    if (!id) return;
    const ids = getStoredProductIds();
    const updated = [id, ...ids.filter((storedId) => storedId !== id)];
    setStoredProductIds(updated);
  },
  clearProducts: () => {
    try {
      localStorage.removeItem(ID_STORAGE_KEY);
    } catch (error) {}
  },
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    void initRecentlyViewedProducts();
  });
} else {
  void initRecentlyViewedProducts();
}
